import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatDistanceToNow, format } from "date-fns";
import { Facebook, Linkedin, Share2, Twitter } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import "../blog-management/RichTextEditor.css"; // Import shared styles
import "./EnhancedBlogPreview.css"; // Import our enhanced preview styles

interface EnhancedBlogPreviewProps {
  post: {
    title: string;
    content?: string;
    excerpt?: string;
    image_url?: string;
    slug?: string;
    published_at?: Date | null;
    metadata?: {
      sources?: Array<{
        description: string;
        url: string;
      }>;
      author?: string;
      categories?: string[];
      tags?: string[];
      unsplash_attribution?: {
        photographerName: string;
        photographerUsername: string;
        photographerUrl: string;
        unsplashUrl: string;
      };
    };
  };
}

const EnhancedBlogPreview = ({ post }: EnhancedBlogPreviewProps) => {
  const now = new Date();
  const publishDate = post.published_at || now;
  const formattedDate = format(publishDate, "MMMM d, yyyy");
  const readingTime = estimateReadingTime(post.content || "");
  const hasSources = post.metadata?.sources && post.metadata.sources.length > 0;
  const hasCategories = post.metadata?.categories && post.metadata.categories.length > 0;
  const hasTags = post.metadata?.tags && post.metadata.tags.length > 0;

  // Estimate reading time based on content length
  function estimateReadingTime(content: string): number {
    const wordsPerMinute = 200;
    const wordCount = content.split(/\s+/).length;
    return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
  }

  // Convert first letter of author name to avatar fallback
  function getInitials(name: string = ""): string {
    if (!name) return "U";
    return name.charAt(0).toUpperCase();
  }

  return (
    <div className="enhanced-preview-container max-w-3xl mx-auto font-sans">
      {/* Article Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight">
          {post.title || "Untitled Post"}
        </h1>
        
        {/* Author and metadata bar */}
        <div className="flex items-center justify-between mb-6 text-sm">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border">
              <AvatarImage src={`https://ui-avatars.com/api/?name=${encodeURIComponent(post.metadata?.author || 'User')}&background=random`} />
              <AvatarFallback>{getInitials(post.metadata?.author)}</AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium text-slate-900">{post.metadata?.author || "Unknown Author"}</div>
              <div className="text-slate-500 text-xs">
                {formattedDate} · {readingTime} min read
              </div>
            </div>
          </div>
          
          {/* Share buttons */}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="rounded-full w-8 h-8 p-0">
              <Twitter className="h-4 w-4 text-slate-700" />
            </Button>
            <Button variant="ghost" size="sm" className="rounded-full w-8 h-8 p-0">
              <Facebook className="h-4 w-4 text-slate-700" />
            </Button>
            <Button variant="ghost" size="sm" className="rounded-full w-8 h-8 p-0">
              <Linkedin className="h-4 w-4 text-slate-700" />
            </Button>
            <Button variant="ghost" size="sm" className="rounded-full w-8 h-8 p-0">
              <Share2 className="h-4 w-4 text-slate-700" />
            </Button>
          </div>
        </div>
        
        {/* Categories */}
        {hasCategories && (
          <div className="mb-4 flex flex-wrap gap-2">
            {post.metadata?.categories.map((category, idx) => (
              <Badge key={idx} variant="secondary" className="font-normal bg-slate-100 text-slate-700 hover:bg-slate-200">
                {category}
              </Badge>
            ))}
          </div>
        )}
      </div>
      
      {/* Featured Image */}
      {post.image_url && (
        <div className="mb-8">
          <div className="relative aspect-[16/9] rounded-lg overflow-hidden">
            <img 
              src={post.image_url} 
              alt={post.title} 
              className="object-cover w-full h-full"
              onError={(e) => {
                e.currentTarget.src = "https://placehold.co/1200x630?text=Featured+Image";
              }}
            />
            
            {/* Unsplash attribution if available */}
            {post.metadata?.unsplash_attribution && (
              <div className="absolute bottom-0 right-0 bg-black/50 text-white p-1 px-2 text-xs">
                Photo by{" "}
                <a 
                  href={post.metadata.unsplash_attribution.photographerUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline hover:text-white/80"
                >
                  {post.metadata.unsplash_attribution.photographerName}
                </a>{" "}
                on{" "}
                <a 
                  href={post.metadata.unsplash_attribution.unsplashUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline hover:text-white/80"
                >
                  Unsplash
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Excerpt */}
      {post.excerpt && (
        <div className="mb-8">
          <p className="text-xl leading-relaxed text-slate-700 font-light border-l-4 border-slate-300 pl-4 italic">
            {post.excerpt}
          </p>
        </div>
      )}
      
      {/* Main Content */}
      <div className="mb-12 prose prose-slate max-w-none lg:prose-lg">
        {post.content ? (
          <div 
            className="ProseMirror"
            dangerouslySetInnerHTML={{ __html: post.content }} 
          />
        ) : (
          <Card className="p-4 bg-slate-50 text-slate-500 text-center">
            No content to preview
          </Card>
        )}
      </div>
      
      {/* Tags */}
      {hasTags && (
        <div className="border-t border-b border-slate-200 py-6 my-6">
          <h3 className="text-sm font-semibold mb-3 text-slate-500 uppercase">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {post.metadata?.tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-sm font-normal">
                #{tag}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {/* Author Bio Card */}
      <div className="author-card mb-8">
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16 border">
            <AvatarImage src={`https://ui-avatars.com/api/?name=${encodeURIComponent(post.metadata?.author || 'User')}&background=random&size=64`} />
            <AvatarFallback>{getInitials(post.metadata?.author)}</AvatarFallback>
          </Avatar>
          
          <div>
            <h3 className="font-bold text-lg mb-1">About {post.metadata?.author || "the Author"}</h3>
            <p className="text-slate-600 text-sm mb-4">
              Content creator and contributor at our blog. Passionate about sharing insights and creating engaging content.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-full">
                <Twitter className="h-4 w-4 mr-1" />
                Twitter
              </Button>
              <Button variant="outline" size="sm" className="rounded-full">
                <Linkedin className="h-4 w-4 mr-1" />
                LinkedIn
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sources Section */}
      {hasSources && (
        <div className="border-t border-slate-200 pt-6 mt-6">
          <h3 className="text-lg font-semibold mb-4">Sources & References</h3>
          <div className="space-y-3">
            {post.metadata?.sources.map((source, index) => (
              <div key={index} className="text-sm">
                <div className="font-medium text-slate-800">{index + 1}. {source.description}</div>
                <a 
                  href={source.url} 
                  className="text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {source.url}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Next/Prev Article Navigation - Mocked */}
      <div className="article-navigation">
        <Button variant="outline" className="flex-1 justify-start">
          <span className="mr-2">←</span> Previous Article
        </Button>
        <Button variant="outline" className="flex-1 justify-end">
          Next Article <span className="ml-2">→</span>
        </Button>
      </div>
    </div>
  );
};

export default EnhancedBlogPreview;
